import {
  MilageRegistrationData,
  MilageRegistrationRecord,
  type ActivityData,
  type ActivityItemsData
} from "app/data/types";
import moment from "moment";
import momentTimezone from "moment-timezone";
import * as Yup from "yup";
import {
  GroupBy,
  convertToNormalTime,
  getDateKey
} from "../reportCommonHelpers";
import { sortBy } from "lodash";

export const reportTransformTimeForAPI = (
  time: string,
  dateValue?: Date,
  isEndTime?: boolean
) => {
  const defaultTime = isEndTime ? "23:59:59" : "00:00:00";

  if (time.indexOf("null") == -1) {
    // Get the date in YYYY-MM-DD format
    const date = dateValue
      ? moment(dateValue).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    // Combine date and time
    const dateTimeString = `${date} ${time}`;

    // Get the user's timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Create a moment with the specific date and time in the user's timezone
    // This ensures we get the correct timezone offset considering DST
    const dateTimeMoment = momentTimezone.tz(
      dateTimeString,
      "YYYY-MM-DD HH:mm:ss",
      userTimeZone
    );

    // Get the timezone offset for this specific date and time
    const timezoneOffset = dateTimeMoment.format("Z");

    if (time === ":00") {
      return `${date}T${defaultTime}${timezoneOffset}`;
    }

    return `${date}T${time}${timezoneOffset}`;
  } else {
    return null;
  }
};

export const getResolvedAddress = (
  row: {
    startLatitude?: number;
    startLongitude?: number;
    endLatitude?: number;
    endLongitude?: number;
  },
  resolvedAddress: Array<{
    latitude: number;
    longitude: number;
    address: string;
  }>,
  key: "start" | "end"
) => {
  const latitudeKey = key === "start" ? "startLatitude" : "endLatitude";
  const longitudeKey = key === "start" ? "startLongitude" : "endLongitude";

  const findAddress = resolvedAddress.find(
    (item: { latitude: number; longitude: number }) =>
      item.latitude === row[latitudeKey as keyof typeof row] &&
      item.longitude === row[longitudeKey as keyof typeof row]
  );

  return findAddress && findAddress?.address ? findAddress.address : null;
};

export const DEFAULT_FILTER_VALUES = {
  driver: [],
  group: [],
  object: [],
  objectType: [],
  ridesModes: []
};

export const customStyles = {
  cells: {
    style: {
      justifyContent: "center"
    }
  }
};

export const exportReportsFormInitialValues = {
  emailAddress: "",
  exportType: 0
};

export const reportsCommonFormInitialValues = {
  objectType: [],
  group: [],
  object: [],
  key: [],
  daysOfWeek: ["0", "1", "2", "3", "4", "5", "6"],
  startTime: null,
  endTime: null,
  assignedToAllObjects: true
};

export const exportReportsFormValidationSchema: Yup.Schema = Yup.object({
  emailAddress: Yup.string().email().required().label("Email"),
  exportType: Yup.number().required("Value is Required")
});

export const commonReportsFilterFormValidationSchema: Yup.Schema = Yup.object({
  objectType: Yup.array().of(Yup.string()).label("Object Types"),
  group: Yup.array().of(Yup.string()).label("Groups"),
  object: Yup.array().of(Yup.string()).label("Objects"),
  key: Yup.array().of(Yup.string()).label("Drivers")
});

export const generateReportFormValidationSchema: Yup.Schema = Yup.object({
  objectType: Yup.array().of(Yup.string()).label("Object Types"),
  group: Yup.array().of(Yup.string()).label("Groups"),
  object: Yup.array().of(Yup.string()).label("Objects"),
  key: Yup.array().of(Yup.string()).label("Drivers")
});

export type FormattedRecord = Omit<
  MilageRegistrationRecord | ActivityItemsData,
  "key"
> & {
  label: string;
};

export type FormattedData = {
  name: string;
  records: {
    label: string;
    data: FormattedRecord[];
  }[];
}[];

export const commonReportsDataFormatter = (
  originalData: MilageRegistrationData | ActivityData,
  identifier: "name" | "object",
  groupBy: GroupBy
): any => {
  let isShowKeyCol = false;
  const formattedDataMap: Map<string, FormattedData> = new Map();

  originalData?.forEach((item: any) => {
    const identifierKey = item?.[identifier];
    let formattedData = formattedDataMap.get(identifierKey);

    if (!formattedData && identifier === "name") {
      const co2EmissionTotal = calculateCO2Emissions(item?.items);

      formattedData = {
        //@ts-ignore
        name: item.name,
        records: [],
        CO2Emissions: `${co2EmissionTotal} g/km`
      };
      //@ts-ignore
      formattedDataMap.set(item.name, formattedData);
    } else if (!formattedData && identifier === "object") {
      formattedData = {
        //@ts-ignore
        name: item.object,
        records: []
      };
      //@ts-ignore
      formattedDataMap.set(item.name, formattedData);
    }

    item?.items?.forEach((record: any) => {
      if (record?.key || record?.rawKeyId) {
        isShowKeyCol = true;
      }
      const dateKey = getDateKey(record.startDate || item?.startDate, groupBy);

      //@ts-ignore
      let dateObject = formattedData.records.find(
        (record: any) => record.label === dateKey
      );

      if (!dateObject) {
        dateObject = {
          label: dateKey,
          data: []
        };
        //@ts-ignore
        formattedData.records.push(dateObject);
      }

      dateObject.data.push({
        ...record
      });
    });
  });

  if (identifier === "object") {
    return sortBy(Array.from(formattedDataMap.values()), ["name"]);
  }

  return {
    // @ts-ignore
    isShowKeyCol,
    formattedData: sortBy(Array.from(formattedDataMap.values()), ["name"])
  };
};

export const convertDatesForFilterPayload = (dateValues: {
  startDate: Date;
  endDate: Date;
}) => {
  const startDate = new Date(dateValues.startDate);
  const endDate = new Date(dateValues.endDate);
  const sameDate = startDate.toDateString() === endDate.toDateString();
  if (sameDate) {
    startDate.setHours(0, 0, 0);
    endDate.setHours(23, 59, 59);
  }
  const formattedStartDate = convertToNormalTime(startDate);
  const formattedEndDate = convertToNormalTime(endDate, true);

  return { formattedStartDate, formattedEndDate };
};

export const calculateCO2Emissions = (data: Array<any>) => {
  if (!data?.length) return 0;
  return data.reduce(
    //@ts-ignore
    (acc: number, currentItem: { cO2Emissions: number }): number => {
      acc = acc + Math.round(currentItem.cO2Emissions);
      return acc;
    },
    0
  );
};

export const calculateTotalBusinessCO2Emissions = (data: Array<any>) => {
  if (!data?.length) return 0;
  return data.reduce(
    //@ts-ignore
    (acc: number, currentItem: { businessEmissionsDriven: number }): number => {
      acc = acc + Math.round(currentItem.businessEmissionsDriven);
      return acc;
    },
    0
  );
};

export const calculateTotalPrivateCO2Emissions = (data: Array<any>) => {
  if (!data?.length) return 0;
  return data.reduce(
    //@ts-ignore
    (acc: number, currentItem: { privateEmissionsDriven: number }): number => {
      acc = acc + Math.round(currentItem.privateEmissionsDriven);
      return acc;
    },
    0
  );
};

export const calculateTotalCommutingCO2Emissions = (data: Array<any>) => {
  if (!data?.length) return 0;
  return data.reduce(
    //@ts-ignore
    (
      acc: number,
      currentItem: { commutingEmissionsDriven: number }
    ): number => {
      acc = acc + Math.round(currentItem.commutingEmissionsDriven);
      return acc;
    },
    0
  );
};
/**
 * Utility function for handling report date ranges in a consistent way
 * @param value Object containing startDate and endDate
 * @returns Formatted date values with proper start and end times
 */
export const getStandardizedReportDates = (value: {
  startDate: Date;
  endDate: Date;
}) => {
  const startDate = new Date(value.startDate);
  const endDate = new Date(value.endDate);
  // Set hours to 00:00:00 for consistent timezone handling
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return {
    startDate,
    endDate
  };
};

export const objectColors = [
  "#E26C62",
  "#D97B17",
  "#81DD7A",
  "#EDED73",
  "#E3B11A",
  "#89CF30",
  "#DA4336",
  "#0DA79A",
  "#25B742",
  "#DA5F27",
  "#05BC85",
  "#4821CB",
  "#EDE61D",
  "#1492AE",
  "#1E48EC",
  "#7700DA",
  "#9F00D4",
  "#7F62B4",
  "#649FEE",
  "#C600CD",
  "#E26C62",
  "#D97B17",
  "#81DD7A",
  "#EDED73",
  "#E3B11A",
  "#89CF30",
  "#DA4336",
  "#0DA79A",
  "#25B742",
  "#DA5F27",
  "#05BC85",
  "#4821CB",
  "#EDE61D",
  "#1492AE",
  "#1E48EC",
  "#7700DA",
  "#9F00D4",
  "#7F62B4",
  "#649FEE",
  "#C600CD",
  "#E26C62",
  "#D97B17",
  "#81DD7A",
  "#EDED73",
  "#E3B11A",
  "#89CF30",
  "#DA4336",
  "#0DA79A",
  "#25B742",
  "#DA5F27",
  "#05BC85",
  "#4821CB",
  "#EDE61D",
  "#1492AE",
  "#1E48EC",
  "#7700DA",
  "#9F00D4",
  "#7F62B4",
  "#649FEE",
  "#C600CD",
  "#E26C62",
  "#D97B17",
  "#81DD7A",
  "#EDED73",
  "#E3B11A",
  "#89CF30",
  "#DA4336",
  "#0DA79A",
  "#25B742",
  "#DA5F27",
  "#05BC85",
  "#4821CB",
  "#EDE61D",
  "#1492AE",
  "#1E48EC",
  "#7700DA",
  "#9F00D4",
  "#7F62B4",
  "#649FEE",
  "#C600CD",
  "#E26C62",
  "#D97B17",
  "#81DD7A",
  "#EDED73",
  "#E3B11A",
  "#89CF30",
  "#DA4336",
  "#0DA79A",
  "#25B742",
  "#DA5F27",
  "#05BC85",
  "#4821CB",
  "#EDE61D",
  "#1492AE",
  "#1E48EC",
  "#7700DA",
  "#9F00D4",
  "#7F62B4",
  "#649FEE",
  "#C600CD",
  "#E26C62",
  "#D97B17",
  "#81DD7A",
  "#EDED73",
  "#E3B11A",
  "#89CF30",
  "#DA4336",
  "#0DA79A",
  "#25B742",
  "#DA5F27",
  "#05BC85",
  "#4821CB",
  "#EDE61D",
  "#1492AE",
  "#1E48EC",
  "#7700DA",
  "#9F00D4",
  "#7F62B4",
  "#649FEE",
  "#C600CD",
  "#E26C62",
  "#D97B17",
  "#81DD7A",
  "#EDED73",
  "#E3B11A",
  "#89CF30",
  "#DA4336",
  "#0DA79A",
  "#25B742",
  "#DA5F27",
  "#05BC85",
  "#4821CB",
  "#EDE61D",
  "#1492AE",
  "#1E48EC",
  "#7700DA",
  "#9F00D4",
  "#7F62B4",
  "#649FEE",
  "#C600CD"
];
